import apiClient from '../../shared/services/apiClient';

/**
 * 发票管理服务（管理员）
 * 用于管理系统发票
 */
export const invoiceService = {
    // ==========================================
    // 发票列表和详情
    // ==========================================

    /**
     * 获取发票列表
     * @param {Object} params - 查询参数
     * @param {number} params.user_id - 用户ID
     * @param {string} params.status - 发票状态 (unpaid, paid, cancelled, void)
     * @param {string} params.invoice_type - 发票类型
     * @param {string} params.start_date - 开始日期
     * @param {string} params.end_date - 结束日期
     * @returns {Promise} 发票列表
     */
    getInvoices: async (params = {}) => {
        try {
            const response = await apiClient.get('/api/admin/invoices', { params });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取单个发票详情
     * @param {number} id - 发票ID
     * @returns {Promise} 发票详细信息（包含明细项）
     */
    getInvoice: async (id) => {
        try {
            const response = await apiClient.get(`/api/admin/invoices/get?id=${id}`);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取发票统计数据
     * @returns {Promise} 统计信息
     */
    getStats: async () => {
        try {
            const response = await apiClient.get('/api/admin/invoices/stats');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 发票管理操作
    // ==========================================

    /**
     * 标记发票为已支付（管理员手动操作）
     * @param {number} id - 发票ID
     * @param {string} paymentMethod - 支付方式（默认'manual'）
     * @returns {Promise} 操作结果
     */
    markAsPaid: async (id, paymentMethod = 'manual') => {
        try {
            const response = await apiClient.post('/api/admin/invoices/mark-paid', {
                id,
                payment_method: paymentMethod
            });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 取消发票
     * @param {number} id - 发票ID
     * @returns {Promise} 取消结果
     */
    cancelInvoice: async (id) => {
        try {
            const response = await apiClient.post('/api/admin/invoices/cancel', { id });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 创建发票
     * @param {Object} data - 发票数据
     * @param {number} data.user_id - 用户ID
     * @param {string} data.invoice_type - 发票类型
     * @param {string} data.currency - 货币（默认CNY）
     * @param {number} data.tax_rate - 税率
     * @param {number} data.discount_amount - 折扣金额
     * @param {string} data.issue_date - 开票日期
     * @param {string} data.due_date - 到期日期
     * @param {string} data.notes - 备注
     * @returns {Promise} 创建的发票
     */
    createInvoice: async (data) => {
        try {
            const response = await apiClient.post('/api/admin/invoices/create', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 更新发票
     * @param {number} id - 发票ID
     * @param {Object} data - 更新数据
     * @returns {Promise} 更新后的发票
     */
    updateInvoice: async (id, data) => {
        try {
            const response = await apiClient.post('/api/admin/invoices/update', { id, ...data });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 删除发票
     * @param {number} id - 发票ID
     * @returns {Promise} 操作结果
     */
    deleteInvoice: async (id) => {
        try {
            const response = await apiClient.post('/api/admin/invoices/delete', { id });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 发票退款（管理员）
     * @param {Object} data - 退款数据
     * @param {number} data.invoice_id - 发票ID
     * @param {number} [data.amount] - 退款金额（不传默认全额）
     * @param {string} [data.target] - 退款去向：'auto' 原路退回（有Stripe支付则退卡，否则退余额）| 'balance' 退到余额
     * @returns {Promise} 退款结果
     */
    refundInvoice: async (data) => {
        try {
            const response = await apiClient.post('/api/admin/invoices/refund', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 重新计算发票金额
     * @param {number} id - 发票ID
     * @returns {Promise} 更新后的发票
     */
    recalculateInvoice: async (id) => {
        try {
            const response = await apiClient.post('/api/admin/invoices/recalculate', { id });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 获取可用产品列表（用于添加发票项）
     * @returns {Promise} 产品列表
     */
    getProducts: async () => {
        try {
            const response = await apiClient.get('/api/admin/invoices/products');
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 发票项目管理
    // ==========================================

    /**
     * 添加发票项
     * @param {Object} data - 项目数据
     * @param {number} data.invoice_id - 发票ID
     * @param {string} data.description - 描述
     * @param {number} data.quantity - 数量
     * @param {number} data.unit_price - 单价
     * @param {number} data.amount - 金额（可选，自动计算）
     * @param {string} data.item_type - 项目类型
     * @param {number} data.product_id - 产品ID（可选）
     * @param {number} data.subscription_id - 订阅ID（可选）
     * @returns {Promise} 添加结果
     */
    addInvoiceItem: async (data) => {
        try {
            const response = await apiClient.post('/api/admin/invoices/items/add', data);
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 更新发票项
     * @param {number} id - 项目ID
     * @param {Object} data - 更新数据
     * @returns {Promise} 更新结果
     */
    updateInvoiceItem: async (id, data) => {
        try {
            const response = await apiClient.post('/api/admin/invoices/items/update', { id, ...data });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    /**
     * 删除发票项
     * @param {number} id - 项目ID
     * @returns {Promise} 删除结果
     */
    deleteInvoiceItem: async (id) => {
        try {
            const response = await apiClient.post('/api/admin/invoices/items/delete', { id });
            return response.data;
        } catch (error) {
            throw error.response?.data || error;
        }
    },

    // ==========================================
    // 常量定义
    // ==========================================

    /**
     * 发票状态枚举
     */
    STATUS: {
        UNPAID: 'unpaid',       // 未支付
        PAID: 'paid',           // 已支付
        CANCELLED: 'cancelled', // 已取消
        VOID: 'void'            // 作废
    },

    /**
     * 发票类型枚举
     */
    TYPES: {
        SUBSCRIPTION: 'subscription', // 订阅
        RENEWAL: 'renewal',           // 续费
        UPGRADE: 'upgrade',           // 升级
        DOWNGRADE: 'downgrade',       // 降级
        REFUND: 'refund',             // 退款
        ADJUSTMENT: 'adjustment',     // 调整
        ADDON: 'addon'                // 附加服务
    },

    /**
     * 支付方式枚举
     */
    PAYMENT_METHODS: {
        BALANCE: 'balance',     // 余额支付
        ALIPAY: 'alipay',       // 支付宝
        WECHAT: 'wechat',       // 微信支付
        BANK: 'bank',           // 银行转账
        MANUAL: 'manual',       // 手动标记
        ONLINE: 'online'        // 在线支付
    }
};

export default invoiceService;
